import React, { useState } from 'react';
import { Chip, Typography } from '@saviynt/design-system';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import { useInvertedLocalize } from '../../utils/localization';

import { CHIPS, CONFIG, KINDS, SIZES } from './constant';
import { invertedMessages } from './messages';
import { pathForSemiCircularSvg } from './utils';

import './RiskScore.css';

function RiskScore({
  kind,
  size,
  value,
  label,
  chipKind,
  className,
  shouldShowZero,
}) {
  const l = useInvertedLocalize(invertedMessages);
  // eslint-disable-next-line no-unused-vars
  const [svgDimensions, setSvgDimensions] = useState(CONFIG[size]);
  // eslint-disable-next-line no-unused-vars
  const [percentage, setPercentage] = useState(
    // eslint-disable-next-line no-nested-ternary
    value ? Math.min(100, Math.max(0, value)) : shouldShowZero ? 0 : '-'
  );

  const progressStatsIconIndicator = classnames(
    'RiskScore-progressStatsIconIndicator',
    `RiskScore-progressStatsIconIndicator--${kind}`,
    className
  );

  const progressStatsNumberColor = classnames(
    `RiskScore-progressStatsNumberColor--${kind}`
  );

  const progressStatsLabelColor = classnames(
    `RiskScore-progressLabel--${kind}`
  );

  return (
    <div
      className={`RiskScore--${size}${
        label?.trim()?.length === 0 && size === SIZES.regular ? '--noLabel' : ''
      }`}>
      <div className='RiskScore-wrapper'>
        <div className='RiskScore-progressStats'>
          <div className='RiskScore-progressStatsIcon'>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width={svgDimensions.width}
              height={svgDimensions.height}
              viewBox={`0 0 ${svgDimensions.width} ${svgDimensions.height}`}
              fill='none'>
              <path
                d={pathForSemiCircularSvg(
                  parseInt(svgDimensions.width / 2, 10),
                  parseInt(svgDimensions.height / 2, 10),
                  parseInt(
                    svgDimensions.width / 2 - svgDimensions.strokeWidth,
                    10
                  ),
                  -parseInt(svgDimensions.baseSpanInDegree / 2, 10),
                  parseInt(svgDimensions.baseSpanInDegree / 2, 10)
                )}
                fill='none'
                stroke='#EBECF2'
                strokeWidth={svgDimensions.strokeWidth}
                strokeLinecap='round'
              />
              {typeof percentage === 'number' && (
                <path
                  className={progressStatsIconIndicator}
                  d={pathForSemiCircularSvg(
                    parseInt(svgDimensions.width / 2, 10),
                    parseInt(svgDimensions.height / 2, 10),
                    parseInt(
                      svgDimensions.width / 2 - svgDimensions.strokeWidth,
                      10
                    ),
                    -parseInt(svgDimensions.baseSpanInDegree / 2, 10),
                    -parseInt(svgDimensions.baseSpanInDegree / 2, 10) +
                      (percentage / 100) * svgDimensions.baseSpanInDegree
                  )}
                  fill='none'
                  stroke='#028329'
                  strokeWidth={svgDimensions.strokeWidth}
                  strokeLinecap='round'
                />
              )}
            </svg>
          </div>
          <div className='RiskScore-progressStatsNumber'>
            <Typography
              kind={size === SIZES.regular ? 'h2' : 'body2'}
              className={progressStatsNumberColor}>
              {percentage}
            </Typography>
          </div>
        </div>
        {size === SIZES.small && (
          <div className='RiskScore-progressChip--small'>
            <Typography kind='body3' className={progressStatsLabelColor}>
              {l(label)}
            </Typography>
          </div>
        )}
        {size === SIZES.regular && label?.trim().length > 0 && (
          <div className='RiskScore-progressChip--regular'>
            <Chip kind={chipKind} label={l(label)} size='small' color={kind} />
          </div>
        )}
      </div>
    </div>
  );
}

RiskScore.propTypes = {
  kind: PropTypes.oneOf(Object.values(KINDS)),
  size: PropTypes.oneOf(Object.values(SIZES)),
  value: PropTypes.number,
  label: PropTypes.string,
  chipKind: PropTypes.oneOf(Object.values(CHIPS)),
  className: PropTypes.string,
  shouldShowZero: PropTypes.bool,
};

RiskScore.defaultProps = {
  kind: KINDS.info,
  size: SIZES.small,
  value: 0,
  label: 'None',
  chipKind: CHIPS.outlined,
  className: null,
  shouldShowZero: false,
};

export default RiskScore;
